import React, { Fragment } from "react";
import {
  Box,
  Heading,
  HStack,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Bar, Line } from "react-chartjs-2";
import type { TFunction } from "i18next";
import { aggregateChartDatasets, aggregateSummaryValues } from "./reviewAggregation";
import { buildLifecycleFillPlugin, buildCumulativeFillPlugin } from "./reviewChartFillPlugins";
import { formatNumber, hasDecimal, formatDateByLocale, resolveDisplayPartNo } from "../../../utils/utils";

/**
 * PDF出力専用：振り返り3画面コンテンツ（縦並べ・タブ/セレクタなし・重複なし）。
 *
 * 画面JSXは現状の切替UIをそのまま維持し、PDF出力時のみこのコンポーネントが
 * `display: block` でマウントされる。各ブロックは `<Box data-pdf-block="...">` でラップし、
 * 上位（ReportAndDraft.tsx）の capturePdfReview がブロック単位でキャプチャしてPDFに追記する。
 */

const RD_REVIEW_PREFIX = "features.InvestmentScreening.ReportAndDraft.review";

interface PdfReviewContentProps {
  /** 通貨換算済みの営業視点 chartData（ReportAndDraftのuseMemo結果を流用） */
  displayReviewSalesChartData: any;
  /** 通貨換算済みの事業視点 chartData / tables / nonCurrencyCharts */
  displayReviewBusinessChartData: any;
  /** 今後の見込み（テキスト8項目） */
  reviewFutureOutlook: any;
  /** 営業視点 テキスト類 */
  reviewSalesText: any;
  /** 事業視点 テキスト類 */
  reviewBusinessText: any;
  /** 振り返り日（YYYY-MM-DD など）。最上位「振り返り（[振り返り日]）」見出しに埋め込む */
  reviewDate: string | null;
  /** 簡易モード */
  isSimpleMode: boolean;
  /** 翻訳関数 */
  t: TFunction;
  /** 選択通貨名(通貨変換計算には未使用→フォーマット処理用営業視点画面などと合わせるためと選択した通貨で参照する値の切り替えで使用) */
  selectedCurrencyName: string;
}

/**
 * 営業視点の Bar チャート options（振り返り境界縦線 + x軸自動間引き + Y軸 suggestedMax）。
 * ReportAndDraft 側 buildSalesChartOptions と同等仕様。
 */
const buildSalesChartOptions = (boundaryIdx: number, labelLen: number, chartData: any, t: TFunction) => {
  const showBoundary = boundaryIdx > 0 && boundaryIdx < labelLen;
  const allValues: number[] = (chartData?.datasets ?? [])
    .flatMap((ds: any) => (Array.isArray(ds.data) ? ds.data : []))
    .filter((v: any) => typeof v === "number" && isFinite(v));
  const unifiedMax = allValues.length > 0
    ? (Math.ceil((Math.max(...allValues, 0)) * 1.1) || 1)
    : undefined;
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      legend: { position: "top" as const },
      datalabels: false as any,
      annotation: showBoundary
        ? {
          annotations: {
            reviewBoundary: {
              type: "line" as const,
              xMin: boundaryIdx - 0.5,
              xMax: boundaryIdx - 0.5,
              borderColor: "rgba(229, 62, 62, 0.8)",
              borderWidth: 2,
              borderDash: [6, 4],
              label: {
                display: true,
                content: t("common.label.review"),
                position: "start" as const,
                backgroundColor: "rgba(229, 62, 62, 0.8)",
                color: "white",
                font: { size: 10 },
              },
            },
          },
        }
        : {},
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: Math.min(12, labelLen),
          font: { size: 10 },
        },
      },
      y: {
        beginAtZero: true,
        ...(unifiedMax !== undefined ? { suggestedMax: unifiedMax } : {}),
        ticks: { maxTicksLimit: 8 },
      },
    },
  };
};

const fmtVal = (v: number, selectedCurrencyName: string, isMoney: boolean, summaryType: string) => {
  if (isMoney) {
    // 末尾の0を除去して表示（14.380 → 14.38）
    const dp = hasDecimal(selectedCurrencyName) ? 4 : 0;
    const formatted = formatNumber(v, selectedCurrencyName, dp);
    if (hasDecimal(selectedCurrencyName) && formatted.includes('.')) {
      return formatted.replace(/0+$/, '').replace(/\.$/, '');
    }
    return formatted;
  }
  return summaryType === "total" ? Math.round(v).toLocaleString() : v.toFixed(1);
};

/**
 * サマリーカード3枚（実績ベース / 軌道修正後 / 前回予測の精度）を描画する共通ヘルパー。
 */
const renderSummaryCards = (sv: any, t: TFunction, selectedCurrencyName: string, isMoney: boolean, summaryType: string,
  displayReviewChartData: { monthLabels: string[], boundary: number, prevBoundary: number }) => (
  <SimpleGrid columns={3} spacing={3} mt={2}>
    {[
      {
        /// 実績ベースの計画比
        label: t(`features.InvestmentScreening.Review.common.summaryActualVsPlan`,
          {
            from: displayReviewChartData.monthLabels[0],
            to: displayReviewChartData.monthLabels[Math.min(displayReviewChartData.boundary, displayReviewChartData.monthLabels.length) - 1]
          }),
        planLabel: t(`common.label.plan`),
        planValue: fmtVal(sv?.actualVsPlan?.planValue, selectedCurrencyName, isMoney, summaryType),
        actualLabel: t(`common.label.actuals`),
        actualValue: fmtVal(sv?.actualVsPlan?.actualValue, selectedCurrencyName, isMoney, summaryType),
        rate: sv?.actualVsPlan?.rate,
      },
      {
        // 軌道修正後の計画比
        label: t("features.InvestmentScreening.Review.common.summaryAdjustedPlan",
          {
            from: displayReviewChartData.monthLabels[0],
            to: displayReviewChartData.monthLabels[displayReviewChartData.monthLabels.length - 1]
          }),
        planLabel: t(`common.label.plan`),
        planValue: fmtVal(sv?.finalOutlook.planTotal, selectedCurrencyName, isMoney, summaryType),
        actualLabel: t(`common.label.outlook`),
        actualValue: fmtVal(sv?.finalOutlook.outlookTotal, selectedCurrencyName, isMoney, summaryType),
        rate: sv?.finalOutlook?.rate,
      },
      {
        // 前回予測の精度
        label: t("features.InvestmentScreening.Review.common.summaryForecastAccuracy",
          {
            from: displayReviewChartData.monthLabels[Math.min(displayReviewChartData.prevBoundary, displayReviewChartData.monthLabels.length - 1)],
            to: displayReviewChartData.monthLabels[Math.min(displayReviewChartData.boundary, displayReviewChartData.monthLabels.length) - 1]
          }),
        planLabel: t(`common.label.previousEstimate`),
        planValue: fmtVal(sv?.forecastAccuracy.prevEstimate, selectedCurrencyName, isMoney, summaryType),
        actualLabel: t(`common.label.actuals`),
        actualValue: fmtVal(sv?.forecastAccuracy.currActual, selectedCurrencyName, isMoney, summaryType),
        rate: sv?.forecastAccuracy?.rate,
      },
    ].map((card, idx) => {
      const r = typeof card.rate === "number" ? card.rate : null;
      const color = r === null ? "gray.500" : r < -10 ? "red.600" : r < 0 ? "orange.500" : "green.600";
      return (
        <Box key={idx} p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
          <Text fontSize="xs" color="gray.500" mb={1}>{card.label}</Text>
          <HStack>
            <Text fontSize="xs" color="gray.500">{card.planLabel} {card.planValue}</Text>
            <Text fontSize="xs" color="gray.500"> {t(`common.label.arrowTo`)} </Text>
            <Text fontSize="xs" color="black" fontWeight="bold">{card.actualLabel} {card.actualValue}</Text>
          </HStack>
          <Text fontSize="lg" fontWeight="bold" color={color}>
            {r === null ? "-" : `${r >= 0 ? "+" : ""}${r.toFixed(1)}%`}
          </Text>
        </Box>
      );
    })}
  </SimpleGrid>
);

/**
 * 1セクション分（チャート + サマリーカード3枚）の表示Box。
 * ReportAndDraft 側 renderSalesSectionBox と同等仕様。chart key には "pdf-" プレフィックスを付与し、
 * 画面用 Chart.js インスタンスとの衝突を回避する。
 */
const renderSalesSectionBox = (
  key: string,
  title: string,
  unit: string,
  chartData: any,
  sv: any,
  salesBoundaryIdx: number,
  t: TFunction,
  selectedCurrencyName: string,
  summaryType: string,
  displayReviewChartData: { monthLabels: string[], boundary: number, prevBoundary: number },
) => {
  const labelLen: number = chartData?.labels?.length ?? 0;
  const opts = buildSalesChartOptions(salesBoundaryIdx, labelLen, chartData, t);
  return (
    <Box mb={6} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
      <Box mb={3}>
        <Text fontSize="md" fontWeight="bold">{title}</Text>
        <Text fontSize="xs" color="gray.500">
          {t(`${RD_REVIEW_PREFIX}.unitPrefix`)}{unit}
        </Text>
      </Box>
      {chartData && (
        <Box h="280px" mb={2}>
          <Bar key={`pdf-${key}`} data={chartData} options={opts as any} />
        </Box>
      )}
      {renderSummaryCards(sv, t, selectedCurrencyName, unit !== "pcs", summaryType, displayReviewChartData)}
    </Box>
  );
};

/**
 * 事業視点 Chart options（営業利益 Line / 製造原価 Bar / 生産数量 Line / 設備稼働率 Bar 共通）。
 * 営業利益のみ tooltip mode "index" を有効にする。ReportAndDraft 側 buildBusinessChartOptions と同等仕様。
 */
const buildBusinessChartOptions = (
  boundaryIdx: number,
  chartData: any,
  t: TFunction,
  opts?: { lineInteraction?: boolean },
) => {
  const labelLen: number = chartData?.labels?.length ?? 0;
  const showBoundary = boundaryIdx > 0 && boundaryIdx < labelLen;
  const allValues: number[] = (chartData?.datasets ?? [])
    .flatMap((ds: any) => (Array.isArray(ds.data) ? ds.data : []))
    .filter((v: any) => typeof v === "number" && isFinite(v));
  const unifiedMax = allValues.length > 0
    ? (Math.ceil((Math.max(...allValues, 0)) * 1.1) || 1)
    : undefined;
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    ...(opts?.lineInteraction ? { interaction: { mode: "index" as const, intersect: false } } : {}),
    plugins: {
      legend: { position: "top" as const },
      datalabels: false as any,
      annotation: showBoundary
        ? {
          annotations: {
            reviewBoundary: {
              type: "line" as const,
              xMin: boundaryIdx - 0.5,
              xMax: boundaryIdx - 0.5,
              borderColor: "rgba(229, 62, 62, 0.8)",
              borderWidth: 2,
              borderDash: [6, 4],
              label: {
                display: true,
                content: t("common.label.review"),
                position: "start" as const,
                backgroundColor: "rgba(229, 62, 62, 0.8)",
                color: "white",
                font: { size: 10 },
              },
            },
          },
        }
        : {},
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: Math.min(12, labelLen),
          font: { size: 10 },
        },
      },
      y: {
        beginAtZero: true,
        ...(unifiedMax !== undefined ? { suggestedMax: unifiedMax } : {}),
        ticks: { maxTicksLimit: 8 },
      },
    },
  };
};

/**
 * 事業視点用 Chart（Line/Bar 切替）描画ヘルパー。
 * 任意のChart.jsプラグイン配列を渡せる（生産数量の差分塗りつぶし等に使用）。
 */
const renderBusinessChart = (
  chartType: "line" | "bar",
  chartData: any,
  boundaryIdx: number,
  t: TFunction,
  keyPrefix: string,
  plugins?: any[],
) => {
  const opts = buildBusinessChartOptions(boundaryIdx, chartData, t, {
    lineInteraction: chartType === "line",
  });
  return chartType === "line"
    ? <Line key={`pdf-${keyPrefix}`} data={chartData} options={opts as any} plugins={plugins as any} />
    : <Bar key={`pdf-${keyPrefix}`} data={chartData} options={opts as any} plugins={plugins as any} />;
};

/**
 * 事業視点 営業利益・製造原価セクション（タイトル + Chart + サマリーカード）。
 */
const renderBizSectionBox = (
  key: string,
  title: string,
  unit: string,
  chartData: any,
  sv: any,
  chartType: "line" | "bar",
  boundaryIdx: number,
  t: TFunction,
  selectedCurrencyName: string,
  summaryType: string,
  displayReviewChartData: { monthLabels: string[], boundary: number, prevBoundary: number },
) => (
  <Box mb={6} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
    <Box mb={3}>
      <Text fontSize="md" fontWeight="bold">{title}</Text>
      <Text fontSize="xs" color="gray.500">{t(`${RD_REVIEW_PREFIX}.unitPrefix`)}{unit}</Text>
    </Box>
    {chartData && (
      <Box h="280px" mb={3}>
        {renderBusinessChart(chartType, chartData, boundaryIdx, t, key)}
      </Box>
    )}
    {renderSummaryCards(sv, t, selectedCurrencyName, unit !== "pcs", summaryType, displayReviewChartData)}
  </Box>
);

/**
 * 事業視点 生産数量セクション（タイトル + lifecycle Chart + 累積 Chart + サマリーカード）。
 */
const renderProdCapBox = (
  key: string,
  title: string,
  unit: string,
  chartData: any,
  cumulativeChartData: any,
  sv: any,
  boundaryIdx: number,
  t: TFunction,
  selectedCurrencyName: string,
  summaryType: string,
  displayReviewChartData: { monthLabels: string[], boundary: number, prevBoundary: number },
) => (
  <Box mb={6} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
    <Box mb={3}>
      <Text fontSize="md" fontWeight="bold">{title}</Text>
      <Text fontSize="xs" color="gray.500">{t(`${RD_REVIEW_PREFIX}.unitPrefix`)}{unit}</Text>
    </Box>
    {chartData && (
      <Box h="280px" mb={3}>
        {renderBusinessChart("line", chartData, boundaryIdx, t, `${key}-pc`, [buildLifecycleFillPlugin(boundaryIdx)])}
      </Box>
    )}
    {cumulativeChartData && (
      <>
        <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={1}>
          {t(`${RD_REVIEW_PREFIX}.nonCurrency.productionCapacityCumulative`)}
        </Text>
        <Box h="280px" mb={3}>
          {renderBusinessChart("line", cumulativeChartData, boundaryIdx, t, `${key}-pc-cum`, [buildCumulativeFillPlugin(boundaryIdx)])}
        </Box>
      </>
    )}
    {sv && renderSummaryCards(sv, t, selectedCurrencyName, unit !== "pcs", summaryType, displayReviewChartData)}
  </Box>
);

/**
 * テキストブロック分割設定：1チャンク = 40行（改行ベース）。
 * 1つのテキスト項目が40行を超える場合、複数の `data-pdf-block` に分割し、PDF出力時の改ページが
 * 自然な境界（行単位）で行われるようにする（addReviewBlockFlow のスライス分割を回避する目的）。
 */
const TEXT_LINES_PER_CHUNK = 40;

/**
 * 改行コードを `\n` に正規化したうえで、`maxLines` 行ごとにチャンク化する。
 * 末尾の空行は1つだけ許容（複数末尾空行は除去）。
 */
const splitTextByLines = (value: string, maxLines: number = TEXT_LINES_PER_CHUNK): string[] => {
  const normalized = (value ?? "").replace(/\r\n?/g, "\n");
  const lines = normalized.split("\n");
  // 末尾の余計な空行は1つに抑える
  while (lines.length > 1 && lines[lines.length - 1] === "" && lines[lines.length - 2] === "") {
    lines.pop();
  }
  if (lines.length === 0) return [""];
  const chunks: string[] = [];
  for (let i = 0; i < lines.length; i += maxLines) {
    chunks.push(lines.slice(i, i + maxLines).join("\n"));
  }
  return chunks;
};

/**
 * テキスト項目を「1チャンク=1 data-pdf-block」のJSX配列にして返す。
 * - チャンクが1つのみ（m=1）: 見出しは `label` のみ
 * - チャンクが複数（m≥2）: 全チャンクに「label（n/m）」表記を付与
 * - 呼び出し側は `prepend` で先頭ブロックにグループ見出し（「コメント」等）を差し込める
 *
 * @param blockKeyPrefix data-pdf-block の接頭辞（例: "reviewSales-comment-marketShare"）
 * @param label 項目見出し（例: "売上計画の対比"）
 * @param value テキスト本体
 * @param t 翻訳関数
 * @param prepend 先頭ブロックの label の上に追加で描画したいJSX（グループ見出し用）
 */
const renderTextItemChunks = (
  blockKeyPrefix: string,
  label: string,
  value: string,
  t: TFunction,
  prepend?: React.ReactNode,
  firstChunkAttrs: Record<string, string> = {},
): React.ReactNode[] => {
  const chunks = splitTextByLines(value);
  const m = chunks.length;
  return chunks.map((chunkText, idx) => {
    const isFirst = idx === 0;
    const displayLabel = m === 1
      ? label
      : t(`${RD_REVIEW_PREFIX}.paginatedLabel`, { label, n: idx + 1, m });
    return (
      <Box
        key={`${blockKeyPrefix}-${idx}`}
        data-pdf-block={`${blockKeyPrefix}-${idx}`}
        {...(isFirst ? firstChunkAttrs : {})}
        mb={2}
        p={3}
        bg="gray.50"
        borderRadius="md"
      >
        {isFirst && prepend}
        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
          {displayLabel}
        </Text>
        <Box p={2} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
          <Text fontSize="sm" whiteSpace="pre-wrap">{chunkText}</Text>
        </Box>
      </Box>
    );
  });
};

/**
 * 営業視点 振り返り：compareMode×PartNo繰り返し + テキスト類1回。
 * 最上位「振り返り（[振り返り日]）」見出しと「営業視点 振り返り」見出しは同一ブロックに統合し、
 * 単独でキャプチャされて高さがほぼ無いページが生成されるのを防ぐ。
 */
const renderReviewSalesSection = (
  displayReviewSalesChartData: any,
  reviewSalesText: any,
  reviewDate: string | null,
  isSimpleMode: boolean,
  t: TFunction,
  selectedCurrencyName: string,
): React.ReactNode => {
  if (!displayReviewSalesChartData) return null;
  const salesBoundaryIdx: number = displayReviewSalesChartData.boundary ?? 0;

  /**
   * 営業視点テキストのラベルを翻訳する（キーが未翻訳の場合はキー自体を返す）
   * @param group テキスト種別グループ名
   * @param key テキストキー
   * @returns 翻訳済みラベル文字列
   */
  const resolveSalesLabel = (
    group: "salesTextLabel" | "salesSupplementaryLabel" | "salesCommentLabel",
    key: string,
  ): string => {
    const fullKey = `${RD_REVIEW_PREFIX}.${group}.${key}`;
    const translated = t(fullKey);
    return translated === fullKey ? key : translated;
  };

  const SALES_SCHEDULE_KEY = "sales_schedule";
  const textValues = reviewSalesText?.textValues || {};
  const supplementaryValues = reviewSalesText?.supplementaryValues || {};
  const comments = reviewSalesText?.comments || {};
  const scheduleEntry = textValues[SALES_SCHEDULE_KEY];
  const otherTextEntries = Object.entries(textValues).filter(([k, v]) => k !== SALES_SCHEDULE_KEY && v);
  const supplementaryEntries = Object.entries(supplementaryValues).filter(([, v]) => v);
  const commentEntries = Object.entries(comments).filter(([, v]) => v);

  return (
    <>
      {/* 最上位「振り返り（[振り返り日]）」見出し + 営業視点 見出し（1ブロックに統合） */}
      <Box data-pdf-block="reviewSales-rootHeader" mb={4} p={4} bg="white">
        <Heading as="h2" size="md" mb={3} pb={2} borderBottom="3px solid" borderColor="gray.700">
          {t(`${RD_REVIEW_PREFIX}.heading`, { date: reviewDate ? formatDateByLocale(reviewDate, false, true, "-") : "" })}
        </Heading>
        <Heading as="h3" size="sm" pb={2} borderBottom="2px solid" borderColor="gray.300">
          {t(`${RD_REVIEW_PREFIX}.salesSection`)}
        </Heading>
      </Box>

      {/* compareMode × チャート系 */}
      {(["previousMode", "originalPlanMode"] as const).map((modeKey) => {
        const modeData = displayReviewSalesChartData[modeKey];
        if (!modeData) return null;
        const hasSections = !!modeData?.sections?.length;
        const hasPriceByPN = !!modeData?.pricePartNoSections?.length;
        const hasQtyByPN = !!modeData?.qtyPartNoSections?.length;
        if (!hasSections && !hasPriceByPN && !hasQtyByPN) return null;

        const isPrev = modeKey === "previousMode";
        const headerColor = isPrev ? "blue.600" : "green.600";
        const headerLabel = isPrev
          ? t(`${RD_REVIEW_PREFIX}.compareModePrev`)
          : t(`${RD_REVIEW_PREFIX}.compareModePlan`);
        const modeId = isPrev ? "previous" : "originalPlan";

        // 最初のセクションブロックに compareMode 見出しを統合する（見出しが単独ページになるのを防ぐ）
        const compareHeaderEl = (
          <Box mb={3} p={2} bg={headerColor} color="white" borderRadius="md">
            <Text fontSize="sm" fontWeight="bold">{headerLabel}</Text>
          </Box>
        );

        let compareHeaderConsumed = false;
        // originalPlanMode の最初のブロックは強制改ページする（前回値との比較と区切るため）
        /**
         * ブロックを compareMode 見出しとともにラップする。
         * 最初の呼び出し時のみ compareMode 見出しを先頭に統合し、以降は content をそのまま Box で包む。
         * originalPlanMode の最初のブロックには data-pdf-page-break="before" を付与する。
         * @param key data-pdf-block のキー
         * @param content ブロック本体の JSX
         * @param extraAttrs Box に追加する任意属性（改ページ指示などに使用）
         */
        const wrapWithCompareHeader = (
          key: string,
          content: React.ReactNode,
          extraAttrs: Record<string, string> = {},
        ) => {
          const isFirstInMode = !compareHeaderConsumed;
          const pageBreak = isFirstInMode && !isPrev ? "before" : undefined;
          if (compareHeaderConsumed) {
            return <Box key={key} data-pdf-block={key} {...(pageBreak ? { "data-pdf-page-break": pageBreak } : {})} {...extraAttrs}>{content}</Box>;
          }
          compareHeaderConsumed = true;
          return (
            <Box key={key} data-pdf-block={key} {...(pageBreak ? { "data-pdf-page-break": pageBreak } : {})} {...extraAttrs}>
              {compareHeaderEl}
              {content}
            </Box>
          );
        };

        // PartNo繰り返し群（販売単価・販売数量）の最後のブロックは「終わりで改ページ」を付与
        const priceList = hasPriceByPN ? modeData.pricePartNoSections : [];
        const qtyList = hasQtyByPN ? modeData.qtyPartNoSections : [];
        const lastPriceIdx = priceList.length - 1;
        const lastQtyIdx = qtyList.length - 1;

        return (
          !isSimpleMode && <Fragment key={modeKey}>
            {/* 売上計画の対比（全体集計）：最初のブロックに compareMode 見出しを統合 */}
            {hasSections && modeData.sections.map((sec: any) =>
              wrapWithCompareHeader(
                `reviewSales-${modeId}-salesPlan-${sec.id}`,
                renderSalesSectionBox(`${modeId}-salesPlan-${sec.id}`, t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesAmount`), sec.unit,
                  sec.chartData ?? null, sec.summaryValues[selectedCurrencyName], salesBoundaryIdx, t,
                  selectedCurrencyName, sec.summaryType, displayReviewSalesChartData),
              ),
            )}

            {/* 販売単価 PartNo分繰り返し（"all"なし） */}
            {priceList.map((sec: any, idx: number) =>
              wrapWithCompareHeader(
                `reviewSales-${modeId}-price-${sec.partNo}`,
                renderSalesSectionBox(
                  `${modeId}-price-${sec.partNo}`,
                  t(`${RD_REVIEW_PREFIX}.partNoSectionTitle`, { title: t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesPrice`), partNo: resolveDisplayPartNo(sec) }),
                  sec.unit,
                  sec.chartData ?? null,
                  sec.summaryValues[selectedCurrencyName],
                  salesBoundaryIdx,
                  t,
                  selectedCurrencyName,
                  sec.summaryType,
                  displayReviewSalesChartData
                ),
                idx === lastPriceIdx ? { "data-pdf-page-break": "after" } : {},
              ),
            )}

            {/* 販売数量 "すべて" + PartNo分繰り返し */}
            {hasQtyByPN && (() => {
              const firstSec = qtyList[0];
              if (!firstSec?.chartData?.datasets) return null;
              const aggChartData = aggregateChartDatasets(qtyList, "chartData");
              if (!aggChartData) return null;
              const aggSummary = aggregateSummaryValues(qtyList);
              return (
                <>
                  {wrapWithCompareHeader(
                    `reviewSales-${modeId}-qty-all`,
                    renderSalesSectionBox(
                      `${modeId}-qty-all`,
                      t(`${RD_REVIEW_PREFIX}.partNoSectionTitle`, { title: t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesQuantity`), partNo: t("common.label.all") }),
                      firstSec.unit,
                      aggChartData,
                      aggSummary,
                      salesBoundaryIdx,
                      t,
                      selectedCurrencyName,
                      firstSec.summaryType,
                      displayReviewSalesChartData
                    ),
                  )}
                  {qtyList.map((sec: any, idx: number) =>
                    wrapWithCompareHeader(
                      `reviewSales-${modeId}-qty-${sec.partNo}`,
                      renderSalesSectionBox(
                        `${modeId}-qty-${sec.partNo}`,
                        t(`${RD_REVIEW_PREFIX}.partNoSectionTitle`, { title: t(`${RD_REVIEW_PREFIX}.salesCommentLabel.salesQuantity`), partNo: resolveDisplayPartNo(sec) }),
                        sec.unit,
                        sec.chartData ?? null,
                        sec.summaryValues,
                        salesBoundaryIdx,
                        t,
                        selectedCurrencyName,
                        sec.summaryType,
                        displayReviewSalesChartData
                      ),
                      idx === lastQtyIdx ? { "data-pdf-page-break": "after" } : {},
                    ),
                  )}
                </>
              );
            })()}
          </Fragment>
        );
      })}

      {/* コメント：1項目=1テキストブロック→40行ごとに分解。最初の項目の最初のチャンクにグループ見出し */}
      {commentEntries.flatMap(([k, v], idx) =>
        renderTextItemChunks(
          `reviewSales-comment-${k}`,
          resolveSalesLabel("salesCommentLabel", k),
          String(v),
          t,
          idx === 0 ? (
            <Text fontSize="md" fontWeight="bold" mb={2}>{t(`${RD_REVIEW_PREFIX}.text.comments`)}</Text>
          ) : undefined,
        ),
      )}

      {/* 販売期間（スケジュール対比）：40行ごとに分解 */}
      {scheduleEntry &&
        renderTextItemChunks(
          "reviewSales-salesSchedule",
          resolveSalesLabel("salesTextLabel", SALES_SCHEDULE_KEY),
          String(scheduleEntry),
          t,
        )}

      {/* 所感：1項目=1テキストブロック→40行ごとに分解。最初の項目の最初のチャンクにグループ見出し */}
      {otherTextEntries.flatMap(([k, v], idx) =>
        renderTextItemChunks(
          `reviewSales-impression-${k}`,
          resolveSalesLabel("salesTextLabel", k),
          String(v),
          t,
          idx === 0 ? (
            <Text fontSize="md" fontWeight="bold" mb={2}>{t(`${RD_REVIEW_PREFIX}.text.impression`)}</Text>
          ) : undefined,
        ),
      )}

      {/* 補足視点：1項目=1テキストブロック→40行ごとに分解。最初の項目の最初のチャンクにグループ見出し */}
      {supplementaryEntries.flatMap(([k, v], idx) =>
        renderTextItemChunks(
          `reviewSales-supplementary-${k}`,
          resolveSalesLabel("salesSupplementaryLabel", k),
          String(v),
          t,
          idx === 0 ? (
            <Text fontSize="md" fontWeight="bold" mb={2}>{t(`${RD_REVIEW_PREFIX}.text.supplementary`)}</Text>
          ) : undefined,
        ),
      )}
    </>
  );
};

/**
 * 事業視点 振り返り：compareMode×各セクション + テーブル系 + テキスト類1回。
 */
const renderReviewBusinessSection = (
  displayReviewBusinessChartData: any,
  reviewBusinessText: any,
  t: TFunction,
  selectedCurrencyName: string,
): React.ReactNode => {
  if (!displayReviewBusinessChartData) return null;
  const boundaryIdx: number = displayReviewBusinessChartData.boundary ?? 0;
  const tables = displayReviewBusinessChartData.tables || {};

  /**
   * 事業視点テキストのラベルを翻訳する（キーが未翻訳の場合はキー自体を返す）
   * @param group テキスト種別グループ名
   * @param key テキストキー
   * @returns 翻訳済みラベル文字列
   */
  const resolveBizLabel = (
    group: "businessTextLabel" | "businessCommentLabel",
    key: string,
  ): string => {
    const fullKey = `${RD_REVIEW_PREFIX}.${group}.${key}`;
    const translated = t(fullKey);
    return translated === fullKey ? key : translated;
  };

  /**
   * テーブル値を表示用文字列に整形する（null/undefined は "-"、"回収不能" は翻訳テキスト）
   * @param v 表示する値
   * @param suffix 単位を表すサフィックス文字列
   * @returns 整形済みの表示文字列
   */
  const formatValue = (v: any, suffix: string = ""): string => {
    if (v === null || v === undefined) return "-";
    if (typeof v === "number") return `${v.toLocaleString()}${suffix}`;
    if (typeof v === "string" && v === "回収不能") return t(`${RD_REVIEW_PREFIX}.unit.unrecoverable`);
    return String(v);
  };

  // 営業利益タイトル補正（実画面と揃える）
  /**
   * セクション ID に応じてタイトルを補正して返す（営業利益は専用の「対比」表記を使用）
   * @param id セクション ID
   * @param fallback デフォルトのタイトル文字列
   * @returns 表示用タイトル文字列
   */
  const sectionDisplayTitle = (id: string, fallback: string) => {
    switch (id) {
      case "operatingProfit":
        return t("features.InvestmentScreening.Review.ReviewBusiness.section.operatingProfitComparison");
      case "manufacturingCost":// 製造原価
        return t("features.InvestmentScreening.Review.ReviewBusiness.section.manufacturingCost");
      default:
        return fallback;
    }
  }

  // ReviewBusiness画面準拠の章構成（PDFは全PartNo列挙）:
  //   (1) 単一値比較項目（回収期間・人員数）
  //   (2) 歩留・サイクルタイム（期間平均）— 全体（多数モデル=max/min/avg / 単一モデル=単値）+ PartNo別単値テーブル群を縦に列挙
  // PDF出力時は各カードを独立した data-pdf-block として返し、addReviewBlockFlow の詰め改ページに任せる。
  const RB_PREFIX = "features.InvestmentScreening.Review.ReviewBusiness";

  /**
   * 歩留・サイクルタイム関連のブロックを「ブロック配列」として返す。
   * - 1個目：単一値比較項目（回収期間・人員数）— 存在すれば
   * - 2個目：歩留・サイクルタイム 全体（多数モデル=max/min/avg / 単一モデル=単値）— 存在すれば
   * - 3個目以降：PartNo別カード × N（PartNoが多い場合に1ページを超えても自然に詰め改ページ）
   *
   * 戻り値は「(key, content) のタプル配列」。呼び出し側で wrapWithCompareHeader / 通常Box で包む。
   */
  const buildPaybackHeadcountStatsBlocks = (modeId: string): { key: string; content: React.ReactNode }[] => {
    const yieldByPN = (tables.yieldByPartNo ?? {}) as Record<string, any>;
    const ctByPN = (tables.cycleTimeByPartNo ?? {}) as Record<string, any>;
    const yieldCtPartNos = Array.from(new Set([
      ...Object.keys(yieldByPN),
      ...Object.keys(ctByPN),
    ]));
    const hasSingleValueData = !!tables.payback || !!tables.headcount;
    const hasYieldCtData =
      yieldCtPartNos.length > 0 || tables.yield || tables.cycleTime
      || tables.yieldStats || tables.cycleTimeStats;
    if (!hasSingleValueData && !hasYieldCtData) return [];

    // PartNo が1件のときは個別PartNoブロックで表示するため全体統計（max/min/avg）は出力しない
    const hasStats = (!!tables.yieldStats || !!tables.cycleTimeStats) && yieldCtPartNos.length > 1;
    const blocks: { key: string; content: React.ReactNode }[] = [];

    // (1) 単一値比較項目
    if (hasSingleValueData) {
      blocks.push({
        key: `reviewBusiness-${modeId}-singleValueItems`,
        content: (
          <Box mb={3} p={3} bg="gray.50" borderRadius="md">
            <Text fontSize="md" fontWeight="bold" mb={2}>
              {t(`${RB_PREFIX}.section.singleValueItems`)}
            </Text>
            <Table size="sm" variant="simple" bg="white" borderRadius="md" className="mm-ceas-table" sx={{ tableLayout: "fixed" }}>
              <Thead>
                <Tr>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.item`)}</Th>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.plan`)}</Th>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.prev`)}</Th>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.review`)}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tables.payback && (
                  <Tr>
                    <Td>{t(`${RB_PREFIX}.item.paybackPeriod`)}</Td>
                    <Td>{formatValue(tables.payback.plan, t(`${RD_REVIEW_PREFIX}.unit.month`))}</Td>
                    <Td>{formatValue(tables.payback.prev, t(`${RD_REVIEW_PREFIX}.unit.month`))}</Td>
                    <Td>{formatValue(tables.payback.review, t(`${RD_REVIEW_PREFIX}.unit.month`))}</Td>
                  </Tr>
                )}
                {tables.headcount && (
                  <Tr>
                    <Td>{t(`${RB_PREFIX}.item.headcount`)}</Td>
                    <Td>{formatValue(tables.headcount.plan, t(`${RD_REVIEW_PREFIX}.unit.person`))}</Td>
                    <Td>{formatValue(tables.headcount.prev, t(`${RD_REVIEW_PREFIX}.unit.person`))}</Td>
                    <Td>{formatValue(tables.headcount.review, t(`${RD_REVIEW_PREFIX}.unit.person`))}</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        ),
      });
    }

    if (!hasYieldCtData) return blocks;

    // (2) 歩留・サイクルタイム 全体（多数モデル=max/min/avg / 単一モデル=単値1行）
    //     → 「歩留・サイクルタイム」見出しも先頭に統合（最初のPartNoカードが無い場合に備えて）
    const yieldCycleTimeHeading = (
      <Text fontSize="md" fontWeight="bold" mb={2}>
        {t(`${RB_PREFIX}.yieldCycleTimeFiltered`)}
      </Text>
    );

    if (hasStats) {
      blocks.push({
        key: `reviewBusiness-${modeId}-yieldCt-overall`,
        content: (
          <Box mb={3} p={3} bg="gray.50" borderRadius="md">
            {yieldCycleTimeHeading}
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
              {t(`${RB_PREFIX}.option.overall`)}
            </Text>
            {(["yieldStats", "cycleTimeStats"] as const).map((statKey) => {
              const stats = tables[statKey];
              if (!stats) return null;
              const unitKey = statKey === "yieldStats" ? "percent" : "sec";
              const unit = t(`${RD_REVIEW_PREFIX}.unit.${unitKey}`);
              const title = statKey === "yieldStats"
                ? t(`${RB_PREFIX}.item.yield`)
                : t(`${RB_PREFIX}.item.cycleTime`);
              return (
                <Box key={statKey} mb={3}>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>{title}</Text>
                  <Table size="sm" variant="simple" bg="white" borderRadius="md" className="mm-ceas-table" sx={{ tableLayout: "fixed" }}>
                    <Thead>
                      <Tr>
                        <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.item`)}</Th>
                        <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.max`)}</Th>
                        <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.min`)}</Th>
                        <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.avg`)}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(["plan", "prev", "review"] as const).map((dataKey) => {
                        const row: any = stats[dataKey];
                        if (!row) return null;
                        return (
                          <Tr key={dataKey}>
                            <Td>{t(`${RD_REVIEW_PREFIX}.tableColumns.${dataKey}`)}</Td>
                            <Td>{formatValue(row.max, unit)}</Td>
                            <Td>{formatValue(row.min, unit)}</Td>
                            <Td>{formatValue(row.avg, unit)}</Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              );
            })}
          </Box>
        ),
      });
    } else if ((tables.yield || tables.cycleTime) && yieldCtPartNos.length === 0) {
      // BOMなし（PartNo別データなし）のとき製造ライン単値を表示。
      // PartNo1件以上の場合は (3) のPartNo別ブロックで表示されるためここでは出力しない。
      blocks.push({
        key: `reviewBusiness-${modeId}-yieldCt-overall`,
        content: (
          <Box mb={3} p={3} bg="gray.50" borderRadius="md">
            {yieldCycleTimeHeading}
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
              {t(`${RB_PREFIX}.option.overall`)}
            </Text>
            <Table size="sm" variant="simple" bg="white" borderRadius="md" className="mm-ceas-table" sx={{ tableLayout: "fixed" }}>
              <Thead>
                <Tr>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.item`)}</Th>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.plan`)}</Th>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.prev`)}</Th>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.review`)}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tables.yield && (
                  <Tr>
                    <Td>{t(`${RB_PREFIX}.item.yield`)}</Td>
                    <Td>{formatValue(tables.yield.plan, t(`${RD_REVIEW_PREFIX}.unit.percent`))}</Td>
                    <Td>{formatValue(tables.yield.prev, t(`${RD_REVIEW_PREFIX}.unit.percent`))}</Td>
                    <Td>{formatValue(tables.yield.review, t(`${RD_REVIEW_PREFIX}.unit.percent`))}</Td>
                  </Tr>
                )}
                {tables.cycleTime && (
                  <Tr>
                    <Td>{t(`${RB_PREFIX}.item.cycleTime`)}</Td>
                    <Td>{formatValue(tables.cycleTime.plan, t(`${RD_REVIEW_PREFIX}.unit.sec`))}</Td>
                    <Td>{formatValue(tables.cycleTime.prev, t(`${RD_REVIEW_PREFIX}.unit.sec`))}</Td>
                    <Td>{formatValue(tables.cycleTime.review, t(`${RD_REVIEW_PREFIX}.unit.sec`))}</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        ),
      });
    }

    // (3) PartNo別 — 1 PartNo = 1ブロック（addReviewBlockFlow の詰め改ページに任せる）
    yieldCtPartNos.forEach((pn) => {
      blocks.push({
        key: `reviewBusiness-${modeId}-yieldCt-${pn}`,
        content: (
          <Box mb={2} p={3} bg="gray.50" borderRadius="md">
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>{tables.displayPartNoMap?.[pn] || pn}</Text>
            <Table size="sm" variant="simple" bg="white" borderRadius="md" className="mm-ceas-table" sx={{ tableLayout: "fixed" }}>
              <Thead>
                <Tr>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.item`)}</Th>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.plan`)}</Th>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.prev`)}</Th>
                  <Th w="25%">{t(`${RD_REVIEW_PREFIX}.tableColumns.review`)}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {yieldByPN[pn] && (
                  <Tr>
                    <Td>{t(`${RB_PREFIX}.item.yield`)}（{tables.displayPartNoMap?.[pn] || pn}）</Td>
                    <Td>{formatValue(yieldByPN[pn]?.plan, t(`${RD_REVIEW_PREFIX}.unit.percent`))}</Td>
                    <Td>{formatValue(yieldByPN[pn]?.prev, t(`${RD_REVIEW_PREFIX}.unit.percent`))}</Td>
                    <Td>{formatValue(yieldByPN[pn]?.current, t(`${RD_REVIEW_PREFIX}.unit.percent`))}</Td>
                  </Tr>
                )}
                {ctByPN[pn] && (
                  <Tr>
                    <Td>{t(`${RB_PREFIX}.item.cycleTime`)}（{tables.displayPartNoMap?.[pn] || pn}）</Td>
                    <Td>{formatValue(ctByPN[pn]?.plan, t(`${RD_REVIEW_PREFIX}.unit.sec`))}</Td>
                    <Td>{formatValue(ctByPN[pn]?.prev, t(`${RD_REVIEW_PREFIX}.unit.sec`))}</Td>
                    <Td>{formatValue(ctByPN[pn]?.current, t(`${RD_REVIEW_PREFIX}.unit.sec`))}</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        ),
      });
    });

    return blocks;
  };

  /**
   * 生産数量実績テーブルを会計年度（4月始まり12ヶ月）ブロックに分割して返す。
   * 各年度ブロックを独立した data-pdf-block としてラップし、addReviewBlockFlow の詰め改ページに任せる。
   * 最初の年度ブロックには「生産数量実績」グループ見出しを統合し、改ページを入れる。
   */
  const renderProductionActuals = () => {
    if (!tables.productionActuals || Object.keys(tables.productionActuals).length === 0) return null;
    const actuals = tables.productionActuals as Record<string, number[]>;
    const labels: string[] = displayReviewBusinessChartData.monthLabels || [];
    const partNos = Object.keys(actuals);
    if (partNos.length === 0) return null;
    const monthLen = Math.max(...partNos.map((pn) => (actuals[pn]?.length ?? 0)), labels.length);
    if (monthLen === 0) return null;

    const fiscalYearByIdx: (number | null)[] = Array.from({ length: monthLen }).map((_, i) => {
      const lbl = labels[i];
      if (!lbl) return null;
      const parts = lbl.split(/[/-]/);
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (!isFinite(y) || !isFinite(m)) return null;
      return m >= 4 ? y : y - 1;
    });
    const blocks: { fiscalYear: number | null; indices: number[] }[] = [];
    let currentBlock: { fiscalYear: number | null; indices: number[] } | null = null;
    for (let i = 0; i < monthLen; i++) {
      const fy = fiscalYearByIdx[i];
      if (!currentBlock || currentBlock.fiscalYear !== fy) {
        currentBlock = { fiscalYear: fy, indices: [i] };
        blocks.push(currentBlock);
      } else {
        currentBlock.indices.push(i);
      }
    }
    const blockTitle = (fy: number | null): string => {
      if (fy === null) return t(`${RD_REVIEW_PREFIX}.tables.productionActuals`);
      return `${t(`${RD_REVIEW_PREFIX}.tables.productionActuals`)}（${fy}${t(`${RD_REVIEW_PREFIX}.tableColumns.fiscalYear`)}）`;
    };

    // 各年度ブロックを独立 data-pdf-block として返し、addReviewBlockFlow の詰め改ページに任せる。
    // 最初の年度ブロックにはグループ見出し「生産数量実績」を統合し、出力前に改ページを入れる（区切り強調）。
    return (
      <>
        {blocks.map((block, blockIdx) => {
          const isFirst = blockIdx === 0;
          const blockKey = `reviewBusiness-productionActuals-${block.fiscalYear ?? `idx${blockIdx}`}`;
          return (
            <Box
              key={blockKey}
              data-pdf-block={blockKey}
              {...(isFirst ? { "data-pdf-page-break": "before" } : {})}
              mb={2}
              p={3}
              bg="gray.50"
              borderRadius="md"
            >
              {isFirst && (
                <Text fontSize="md" fontWeight="bold" mb={2}>
                  {t(`${RD_REVIEW_PREFIX}.tables.productionActuals`)}
                </Text>
              )}
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>{blockTitle(block.fiscalYear)}</Text>
              <Table size="sm" variant="simple" bg="white" borderRadius="md" className="mm-ceas-table">
                <Thead>
                  <Tr>
                    <Th>{t(`${RD_REVIEW_PREFIX}.tableColumns.partNo`)}</Th>
                    {block.indices.map((idx) => (
                      <Th key={idx}>{labels[idx] ?? `${idx + 1}${t(`${RD_REVIEW_PREFIX}.tableColumns.month`)}`}</Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {partNos.map((pn) => (
                    <Tr key={pn}>
                      <Td>{tables.displayPartNoMap?.[pn] || pn}</Td>
                      {block.indices.map((idx) => (
                        <Td key={idx}>{formatValue(actuals[pn]?.[idx] ?? null)}</Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          );
        })}
      </>
    );
  };

  const businessTextValues = reviewBusinessText?.textValues || {};
  const businessComments = reviewBusinessText?.comments || {};
  const businessTextEntries = Object.entries(businessTextValues).filter(([, v]) => v);
  const businessCommentEntries = Object.entries(businessComments).filter(([, v]) => v);

  // 事業視点全体で最初のブロックに「事業視点 振り返り」見出しを統合する
  let businessRootHeaderConsumed = false;
  const businessRootHeaderEl = (
    <Box mb={4} mt={2}>
      <Heading as="h3" size="sm" pb={2} borderBottom="2px solid" borderColor="gray.300">
        {t(`${RD_REVIEW_PREFIX}.businessSection`)}
      </Heading>
    </Box>
  );
  /**
   * 事業視点の最初のブロックに「事業視点 振り返り」見出しを1度だけ統合する。
   * 2回目以降の呼び出しでは extra + body をそのまま返す。
   * @param extra compareMode 見出しなどの追加要素（null 可）
   * @param body ブロック本体の JSX
   * @returns 見出しを統合した JSX
   */
  const wrapWithBusinessRootHeader = (extra: React.ReactNode, body: React.ReactNode): React.ReactNode => {
    if (businessRootHeaderConsumed) return <>{extra}{body}</>;
    businessRootHeaderConsumed = true;
    return <>{businessRootHeaderEl}{extra}{body}</>;
  };

  return (
    <>
      {/* compareMode × チャート系 */}
      {(["previousMode", "originalPlanMode"] as const).map((modeKey) => {
        const modeData = displayReviewBusinessChartData[modeKey];
        const ncData = displayReviewBusinessChartData.nonCurrencyCharts?.[modeKey];
        if (!modeData && !ncData) return null;

        const isPrev = modeKey === "previousMode";
        const headerColor = isPrev ? "blue.600" : "green.600";
        const headerLabel = isPrev
          ? t(`${RD_REVIEW_PREFIX}.compareModePrev`)
          : t(`${RD_REVIEW_PREFIX}.compareModePlan`);
        const modeId = isPrev ? "previous" : "originalPlan";

        const compareHeaderEl = (
          <Box mb={3} p={2} bg={headerColor} color="white" borderRadius="md">
            <Text fontSize="sm" fontWeight="bold">{headerLabel}</Text>
          </Box>
        );
        let compareHeaderConsumed = false;
        // 事業視点の各 compareMode の最初のブロックは強制改ページする
        // （previousMode: 営業視点との境界、originalPlanMode: 前回比較と計画比較の境界）
        /**
         * ブロックを compareMode 見出し＋事業視点ルート見出しとともにラップする。
         * - 最初の呼び出し時のみ compareMode 見出しを統合し、強制改ページを付与する
         * - 事業視点全体の最初のブロックにはさらに「事業視点 振り返り」見出しも統合される（wrapWithBusinessRootHeader経由）
         * @param key data-pdf-block のキー
         * @param content ブロック本体の JSX
         * @param extraAttrs Box に追加する任意属性（改ページ指示などに使用）
         */
        const wrapWithCompareHeader = (
          key: string,
          content: React.ReactNode,
          extraAttrs: Record<string, string> = {},
        ) => {
          const isFirstInMode = !compareHeaderConsumed;
          const pageBreak = isFirstInMode ? "before" : undefined;
          const headerPart = isFirstInMode ? compareHeaderEl : null;
          compareHeaderConsumed = true;
          // 事業視点ルート見出しを「事業視点全体の最初のブロック」に1度だけ統合
          const bodyWithBizRoot = wrapWithBusinessRootHeader(headerPart, content);
          return (
            <Box
              key={key}
              data-pdf-block={key}
              {...(pageBreak ? { "data-pdf-page-break": pageBreak } : {})}
              {...extraAttrs}
            >
              {bodyWithBizRoot}
            </Box>
          );
        };

        // 生産数量 PartNo繰り返し群（"all" + PartNo分）の最後のブロックには「終わりで改ページ」
        const pcByPN: any[] = ncData && Array.isArray(ncData.productionCapacityByPartNo)
          ? ncData.productionCapacityByPartNo
          : [];
        const lastPcIdx = pcByPN.length - 1;

        return (
          <Fragment key={modeKey}>
            {/* ① 通貨依存セクション（営業利益・製造原価） */}
            {modeData?.sections?.map((sec: any) => {
              const chartType: "line" | "bar" = sec.id === "operatingProfit" ? "line" : "bar";
              const displayTitle = sectionDisplayTitle(sec.id, sec.title);
              return wrapWithCompareHeader(
                `reviewBusiness-${modeId}-${sec.id}`,
                renderBizSectionBox(
                  `${modeId}-${sec.id}`,
                  displayTitle,
                  sec.unit,
                  sec.chartData ?? null,
                  sec.summaryValues[selectedCurrencyName],
                  chartType,
                  boundaryIdx,
                  t,
                  selectedCurrencyName,
                  sec.summaryType,
                  displayReviewBusinessChartData
                ),
              );
            })}

            {/* ② 通貨非依存：生産数量（"all" + PartNo分繰り返し）+ 設備稼働率 */}
            {ncData && (() => {
              const or = ncData.operatingRate;
              return (
                <>
                  {/* 生産数量 "all" */}
                  {pcByPN.length > 0 && (() => {
                    const firstPc = pcByPN[0];
                    if (!firstPc?.chartData?.datasets) return null;
                    const aggChartData = aggregateChartDatasets(pcByPN, "chartData");
                    const aggCumulative = aggregateChartDatasets(pcByPN, "cumulativeChartData");
                    const aggSummary = aggregateSummaryValues(pcByPN);
                    const prodCapTitleAll = t(`${RD_REVIEW_PREFIX}.partNoSectionTitle`, {
                      title: t(`${RD_REVIEW_PREFIX}.nonCurrency.productionCapacity`),
                      partNo: t("common.label.all"),
                    });
                    // PartNo繰り返しが0件の場合は "all" が群の最後 → 改ページ
                    const isLast = pcByPN.length === 0;
                    return wrapWithCompareHeader(
                      `reviewBusiness-${modeId}-prodCap-all`,
                      renderProdCapBox(
                        `${modeId}-prodCap-all`,
                        prodCapTitleAll,
                        firstPc.unit || t(`${RD_REVIEW_PREFIX}.unit.piece`),
                        aggChartData,
                        aggCumulative,
                        aggSummary,
                        boundaryIdx,
                        t,
                        selectedCurrencyName,
                        firstPc.summaryType,
                        displayReviewBusinessChartData
                      ),
                      isLast ? { "data-pdf-page-break": "after" } : {},
                    );
                  })()}
                  {/* 生産数量 PartNo分繰り返し */}
                  {pcByPN.map((pc: any, idx: number) =>
                    wrapWithCompareHeader(
                      `reviewBusiness-${modeId}-prodCap-${pc.partNo}`,
                      renderProdCapBox(
                        `${modeId}-prodCap-${pc.partNo}`,
                        t(`${RD_REVIEW_PREFIX}.partNoSectionTitle`, {
                          title: t(`${RD_REVIEW_PREFIX}.nonCurrency.productionCapacity`),
                          partNo: resolveDisplayPartNo(pc),
                        }),
                        pc.unit || t(`${RD_REVIEW_PREFIX}.unit.piece`),
                        pc.chartData,
                        pc.cumulativeChartData,
                        pc.summaryValues,
                        boundaryIdx,
                        t,
                        selectedCurrencyName,
                        pc.summaryType,
                        displayReviewBusinessChartData
                      ),
                      idx === lastPcIdx ? { "data-pdf-page-break": "after" } : {},
                    ),
                  )}
                  {/* 設備稼働率 */}
                  {or && wrapWithCompareHeader(
                    `reviewBusiness-${modeId}-operatingRate`,
                    <Box mb={6} p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
                      <Box mb={3}>
                        <Text fontSize="md" fontWeight="bold">{t(`${RD_REVIEW_PREFIX}.nonCurrency.operatingRate`)}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {t(`${RD_REVIEW_PREFIX}.unitPrefix`)}{t(`${RD_REVIEW_PREFIX}.unit.percent`)}
                        </Text>
                      </Box>
                      {or.chartData && (
                        <Box h="280px" mb={3}>
                          {renderBusinessChart("bar", or.chartData, boundaryIdx, t, `${modeId}-operatingRate`)}
                        </Box>
                      )}
                    </Box>,
                  )}
                </>
              );
            })()}

            {/* ③ 統合表（回収期間・人員数・歩留CT統計・PartNo別歩留CT）
                各カードを独立 data-pdf-block として並べ、addReviewBlockFlow の詰め改ページに任せる。
                1個目のブロックは wrapWithCompareHeader で compareMode 見出し+事業視点ルート見出しを統合可能とし、
                それ以降のブロックは通常の <Box data-pdf-block> として並べる。 */}
            {(() => {
              const blocks = buildPaybackHeadcountStatsBlocks(modeId);
              if (blocks.length === 0) return null;
              return blocks.map((b, idx) => {
                if (idx === 0) {
                  return wrapWithCompareHeader(b.key, b.content);
                }
                return (
                  <Box key={b.key} data-pdf-block={b.key}>
                    {b.content}
                  </Box>
                );
              });
            })()}
          </Fragment>
        );
      })}

      {/* ④ 生産数量実績（compareMode非依存・1回のみ） */}
      {renderProductionActuals()}

      {/* ⑤ コメント：1項目=1テキストブロック→40行ごとに分解。最初の項目の最初のチャンクにグループ見出し */}
      {businessCommentEntries.flatMap(([k, v], idx) =>
        renderTextItemChunks(
          `reviewBusiness-comment-${k}`,
          resolveBizLabel("businessCommentLabel", k),
          String(v),
          t,
          idx === 0 ? (
            <Text fontSize="md" fontWeight="bold" mb={2}>{t(`${RD_REVIEW_PREFIX}.text.comments`)}</Text>
          ) : undefined,
        ),
      )}

      {/* ⑥ 所感：1項目=1テキストブロック→40行ごとに分解。最初の項目の最初のチャンクにグループ見出し */}
      {businessTextEntries.flatMap(([k, v], idx) =>
        renderTextItemChunks(
          `reviewBusiness-impression-${k}`,
          resolveBizLabel("businessTextLabel", k),
          String(v),
          t,
          idx === 0 ? (
            <Text fontSize="md" fontWeight="bold" mb={2}>{t(`${RD_REVIEW_PREFIX}.text.impression`)}</Text>
          ) : undefined,
        ),
      )}
    </>
  );
};

/**
 * 今後の見込み 振り返り：①〜⑧テキスト（compareMode非依存・1回のみ）。
 */
const renderReviewFutureSection = (reviewFutureOutlook: any, t: TFunction, selectedCurrencyName: string,): React.ReactNode => {
  if (!reviewFutureOutlook) return null;
  const RD_FUTURE = "features.InvestmentScreening.Review.ReviewFuture";
  const FUTURE_ITEM_IDS = [
    "further_sales",
    "further_productivity",
    "sales_corrective",
    "manufacturing_corrective",
    "consultation",
    "strategy",
    "achievements",
    "continued_investment",
  ] as const;
  const entries = FUTURE_ITEM_IDS
    .map((id) => ({ id, label: t(`${RD_FUTURE}.items.${id}.label`), value: reviewFutureOutlook[id] }))
    .filter((e) => typeof e.value === "string" && e.value.trim().length > 0);
  if (entries.length === 0) return null;

  const futureHeaderEl = (
    <Box mb={4} mt={2}>
      <Heading as="h3" size="sm" pb={2} borderBottom="2px solid" borderColor="gray.300">
        {t(`${RD_REVIEW_PREFIX}.futureSection`)}
      </Heading>
    </Box>
  );

  return (
    <>
      {/* ①〜⑧ 各テキスト：40行ごとに分解。最初の項目の最初のチャンクに「今後の見込み 振り返り」見出しを統合 */}
      {entries.flatMap((e, idx) =>
        renderTextItemChunks(
          `reviewFuture-${e.id}`,
          e.label,
          String(e.value),
          t,
          idx === 0 ? futureHeaderEl : undefined,
          idx === 0 ? { "data-pdf-page-break": "before" } : {},
        ),
      )}
    </>
  );
};

/**
 * PDF出力専用：振り返り3画面コンテンツ。
 */
const PdfReviewContent: React.FC<PdfReviewContentProps> = ({
  displayReviewSalesChartData,
  displayReviewBusinessChartData,
  reviewFutureOutlook,
  reviewSalesText,
  reviewBusinessText,
  reviewDate,
  isSimpleMode,
  t,
  selectedCurrencyName,
}) => {
  return (
    <Box className="pdf-review-content" p={4} bg="white">
      {renderReviewSalesSection(displayReviewSalesChartData, reviewSalesText, reviewDate, isSimpleMode, t, selectedCurrencyName)}
      {renderReviewBusinessSection(displayReviewBusinessChartData, reviewBusinessText, t, selectedCurrencyName)}
      {renderReviewFutureSection(reviewFutureOutlook, t, selectedCurrencyName)}
    </Box>
  );
};

export default PdfReviewContent;
